// src/portal/contexts/illustrations/components/GenerateImageButton.tsx

'use client';

import { RefreshCw, ImagePlus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Spinner } from '@/shared/components/ui/spinner';

interface GenerateImageButtonProps {
  /** An image already exists — show the "regenerate" affordance instead of "generate". */
  hasImage: boolean;
  /** Generation in flight — spinner + disabled. */
  isGenerating: boolean;
  /** Fired on click. Generation is seamless (the server enforces the allowance). */
  onGenerate: () => void;
  labels: { generate: string; regenerate: string; generating: string };
  variant?: React.ComponentProps<typeof Button>['variant'];
  size?: React.ComponentProps<typeof Button>['size'];
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * The ONE image generate/regenerate button. Same markup, icon logic (ImagePlus
 * for a new image, RefreshCw for a regenerate), and loading state everywhere —
 * cover, page image, and character portrait each pass their own labels + handler.
 */
export function GenerateImageButton({
  hasImage,
  isGenerating,
  onGenerate,
  labels,
  variant = 'ghost',
  size = 'sm',
  className = 'h-7 text-xs',
  disabled,
  'data-testid': testId,
}: GenerateImageButtonProps) {
  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      disabled={disabled || isGenerating}
      onClick={onGenerate}
      data-testid={testId}
    >
      {isGenerating ? (
        <><Spinner size="sm" className="mr-1" /><span className="truncate">{labels.generating}</span></>
      ) : hasImage ? (
        <><RefreshCw className="mr-1 h-3 w-3 shrink-0" /><span className="truncate">{labels.regenerate}</span></>
      ) : (
        <><ImagePlus className="mr-1 h-3 w-3 shrink-0" /><span className="truncate">{labels.generate}</span></>
      )}
    </Button>
  );
}
